import request from '@/utils/request'

/**
 * 视频管理接口
 *
 * 影视：
 *   /movies/movies/page                    视频分页（支持 tag_id 筛选，返回项带 tags 数组）
 *   /movies/movies                         批量删除（body 传 cartoonCode 数组）
 *   /movies/movies/{cartoonCode}/status    上下架
 *   /movies/movies/{id}/tags               查询 / 修改单个视频的标签
 *   /movies/tags                           批量修改多个视频的标签
 *
 * 电影标签（已取代旧的 dict/sondict 分类体系）：
 *   /movie_tags/page                       标签分页
 *   /movie_tags                            新建标签
 *   /movie_tags/{id}                       修改标签(名称+排序) / 删除标签
 *   /movie_tags/{id}/sort                  修改排序
 *   /movie_tags/{id}/status                修改状态
 *
 * movies 表仍然没有「排序」字段，该列为假数据（TODO 已标注）。
 */

/**
 * 视频分页
 * @param {Object} params - page / limit / cartoonName / cartoonVip / tag_id
 */
export function getMoviesPage (params) {
  return request({
    url: '/movies/movies/page',
    method: 'get',
    params
  })
}

/**
 * 批量删除视频
 * @param {Array<String>} codes - cartoonCode 数组
 */
export function deleteMovies (codes) {
  return request({
    url: '/movies/movies',
    method: 'delete',
    data: codes
  })
}

/**
 * 修改视频上下架状态
 * 注意：接口文档写的是 POST，实际后端是 PUT，以实际为准
 * @param {String|Number} cartoonCode
 * @param {Number} status - 1=上架 2=下架
 */
export function updateMovieStatus (cartoonCode, status) {
  return request({
    url: `/movies/movies/${cartoonCode}/status`,
    method: 'put',
    data: { status }
  })
}

/**
 * 查询单个视频的标签
 */
export function getMovieTags (cartoonCode) {
  return request({
    url: `/movies/movies/${cartoonCode}/tags`,
    method: 'get'
  })
}

/**
 * 修改单个视频的标签（tagIds 是修改后的完整集合，最多 3 个，空数组表示清空）
 */
export function updateMovieTags (cartoonCode, tagIds) {
  return request({
    url: `/movies/movies/${cartoonCode}/tags`,
    method: 'post',
    data: { tagIds }
  })
}

/**
 * 批量修改多个视频的标签
 * 每个视频原有标签会被整体替换为同一个集合；最多 100 个视频、3 个标签
 * 注意：后端该批量操作不使用事务，中途异常可能产生部分更新
 * @param {Array} movies - cartoonCode 数组
 * @param {Array} tagIds - 标签 id 数组，传空数组表示清空这些视频的标签
 */
export function batchUpdateMovieTags (movies, tagIds) {
  return request({
    url: '/movies/tags',
    method: 'post',
    data: { movies, tagIds }
  })
}

/**
 * 标签分页
 * @param {Object} params - page / limit / keyword / sort_field / sort_order
 */
export function getMovieTagPage (params) {
  return request({
    url: '/movie_tags/page',
    method: 'get',
    params
  })
}

/**
 * 新建标签
 * @param {Object} data - { name, keywords, sort }
 *        name 必填、最长 50 字符且不能重复；sort 省略为 0；新建后状态为 1
 *        keywords 是标签关键词（逗号分隔，≤500 字），省略为空串
 */
export function addMovieTag (data) {
  return request({
    url: '/movie_tags',
    method: 'post',
    data
  })
}

/**
 * 修改标签（名称 + 关键词 + 排序）
 * data.name 传值则改名(非空/≤50/不重复)，省略则只改排序；不改状态
 * data.keywords 省略则不动，传空串表示清空该标签的关键词
 * @param {Object} data - { name, keywords, sort }
 */
export function updateMovieTag (id, data) {
  return request({
    url: `/movie_tags/${id}`,
    method: 'put',
    data
  })
}

/**
 * 删除标签（仍有关联电影时后端会拒绝）
 */
export function deleteMovieTag (id) {
  return request({
    url: `/movie_tags/${id}`,
    method: 'delete'
  })
}

/**
 * 修改标签排序
 */
export function updateMovieTagSort (id, sort) {
  return request({
    url: `/movie_tags/${id}/sort`,
    method: 'post',
    data: { sort }
  })
}

/**
 * 修改标签状态
 * @param {Number} status - 1=正常 2=禁用
 */
export function updateMovieTagStatus (id, status) {
  return request({
    url: `/movie_tags/${id}/status`,
    method: 'post',
    data: { status }
  })
}

/**
 * 视频的分集列表
 * movies 表没有播放地址字段，播放源存在分集表里，
 * 视频弹窗的「播放地址」对应该视频排序最靠前的那条分集
 */
export function getMovieEpisodes (cartoonCode) {
  return request({
    url: '/moviesinfo/moviesinfo/page',
    method: 'get',
    params: { page: 1, limit: 50, cartoonCode }
  })
}

/**
 * 新增 / 修改分集（带 id 为修改）
 * @param {Object} data - { id, url, name, sorts, charge, cartoonCode }
 */
export function saveMovieEpisode (data) {
  return request({
    url: '/moviesinfo/moviesinfo/',
    method: data.id ? 'put' : 'post',
    data
  })
}

/**
 * 清理用户端搜索缓存
 */
export function deleteFrontSearchCache () {
  return request({
    url: '/movies/movies/deleteFrontSearchCache',
    method: 'delete'
  })
}

// 收费类型
export const VIP_TYPE = {
  FREE: 0,
  PAID: 1
}

// 上下架状态
export const MOVIE_STATUS = {
  ONLINE: 1,
  OFFLINE: 2
}

/**
 * 关键词匹配试跑：给一个视频标题，看会命中哪些标签
 * 返回 [{ tagId, tagName, sort, matchedBy: 'name'|'keyword', matchedValue }]，最多 3 条
 * 注意跑的是「已保存」的标签数据，弹窗里没保存的改动不会体现
 */
export function previewMovieTagKeywords (title) {
  return request({
    url: '/movie_tags/keywords/preview',
    method: 'get',
    params: { title }
  })
}

/**
 * 让改完的标签立刻参与匹配
 * 后端默认缓存 300 秒，改完标签不调这个的话试跑结果会滞后
 */
export function reloadMovieTagKeywords () {
  return request({
    url: '/movie_tags/keywords/reload',
    method: 'post'
  })
}

// 标签状态
export const TAG_STATUS = {
  NORMAL: 1,
  DISABLED: 2
}

// 单个视频最多可绑定的标签数
export const MAX_TAGS_PER_MOVIE = 3

// ---------- 假数据 ----------

/**
 * TODO 假数据：排序权重
 * movies 表仍无排序字段，后端补充后删除本方法
 */
export function mockSort (code) {
  const str = String(code || '')
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) % 100000
  }
  return 1 + (hash % 99)
}
